import { buildCommerceSectionRoute } from '@/views/commerce/catalogHierarchyShared';
import { getCommerceHelpArticles } from '@/views/commerce/commerceHelpContent';
import type { PortalHelpArticle } from '@/lib/portal-help';

const baseArticles: PortalHelpArticle[] = [
  {
    articleId: 'getting-started',
    title: 'Getting Started',
    summary:
      'Learn the tenant-first portal flow: sign in, confirm tenant context, start from portal home, and use support or help when you need guidance.',
    category: 'getting-started',
    keywords: ['start', 'getting started', 'portal basics', 'where do I begin', 'help', 'documentation'],
    relatedRoutes: ['/dashboard', '/support', '/settings'],
    relatedArticleIds: ['portal-home', 'tenant-context', 'support', 'settings'],
    overlayContexts: ['/dashboard', '/', '/support'],
    metrics: [
      { label: 'Audience', value: 'Portal users' },
      { label: 'Focus', value: 'Sign in and navigation' },
      { label: 'Best Next Step', value: 'Start from portal home' },
    ],
    sections: [
      {
        id: 'first-steps',
        title: 'First Steps',
        paragraphs: [
          'After sign-in, confirm that the tenant shown in the portal header matches the customer organization you intend to work with.',
          'The portal is tenant-scoped. Most pages, permissions, and data access are resolved from the currently selected tenant.',
        ],
      },
      {
        id: 'where-to-go',
        title: 'Where To Go',
        paragraphs: [
          'Portal home at /dashboard is the normal starting point for users after sign-in.',
          'Use the shell navigation and command/search surface when you need to jump to Maps, Sales, Commerce, Help, Support, or Settings without backing through the current page.',
        ],
      },
      {
        id: 'how-help-works',
        title: 'How Help Works',
        paragraphs: [
          'Use the Help route for full articles, or open contextual help from the current workspace when you need guidance without leaving the screen.',
          'The command palette also searches help content, so natural questions such as where do I switch tenant or how do I find support can lead directly to the right article.',
        ],
      },
    ],
  },
  {
    articleId: 'portal-home',
    title: 'Portal Home',
    summary:
      'Portal home is the tenant-first landing page for signed-in users. It shows current scope, support entry points, and the workspaces available in the current session.',
    category: 'tenant-workspaces',
    keywords: ['portal home', 'dashboard', 'landing page', 'welcome', 'tenant-first'],
    relatedRoutes: ['/dashboard'],
    relatedArticleIds: ['getting-started', 'tenant-context', 'support'],
    overlayContexts: ['/', '/dashboard'],
    metrics: [
      { label: 'Workspace', value: 'Portal home' },
      { label: 'Focus', value: 'Context and navigation' },
      { label: 'Use It For', value: 'Finding the next tenant-scoped task' },
    ],
    sections: [
      {
        id: 'purpose',
        title: 'What Portal Home Is For',
        paragraphs: [
          'Portal home replaces the old account-first landing pattern. It is where users orient themselves after sign-in and choose the next tenant-scoped workflow.',
          'The page emphasizes tenant context, support, setup, and the currently available workspaces instead of dropping users into billing or legacy account details.',
        ],
      },
      {
        id: 'context',
        title: 'What To Check First',
        paragraphs: [
          'Confirm the active tenant and organization before moving into a deeper workflow.',
          'If a workspace is missing, first check the tenant selector and provisioning state before assuming the route is broken.',
        ],
      },
    ],
  },
  {
    articleId: 'tenant-context',
    title: 'Tenant Context',
    summary:
      'The portal always operates in a tenant context. Learn how to confirm the active tenant, switch tenants, and understand why data changes when the tenant changes.',
    category: 'tenant-workspaces',
    keywords: ['tenant', 'switch tenant', 'scope', 'organization', 'why is my data different'],
    relatedRoutes: ['/dashboard', '/settings'],
    relatedArticleIds: ['getting-started', 'portal-home', 'support'],
    overlayContexts: ['/dashboard', '/settings'],
    metrics: [
      { label: 'Object', value: 'Tenant selector' },
      { label: 'Controls', value: 'Workspace scope' },
      { label: 'Used By', value: 'All portal pages' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What Tenant Context Means',
        paragraphs: [
          'The active tenant in the header determines which accounts, features, and permissions the portal will show you.',
          'Changing the tenant can change the list of accounts you can open, the workspaces available in navigation, and the data returned by each route.',
        ],
      },
      {
        id: 'switching',
        title: 'Switching Tenants',
        paragraphs: [
          'Use the tenant selector in the portal header on desktop or the dedicated context bar on mobile.',
          'If only one tenant is available, the portal still identifies it, but no alternate selection list is shown.',
        ],
      },
      {
        id: 'troubleshooting',
        title: 'Common Questions',
        paragraphs: [
          'If a page looks empty or a menu item disappears, first confirm you are on the expected tenant.',
          'If the tenant list does not include the tenant you expect, access is usually determined by membership and provisioning rather than by local browser state.',
        ],
      },
    ],
  },
  {
    articleId: 'accounts-overview',
    title: 'Accounts Overview',
    summary:
      'Account pages now exist as secondary compatibility surfaces for billing-oriented records. They are no longer the primary portal landing experience.',
    category: 'tenant-workspaces',
    keywords: ['accounts', 'account overview', 'billing records', 'compatibility', 'account workspace'],
    relatedRoutes: ['/dashboard'],
    relatedArticleIds: ['account-services', 'billing-payments', 'tenant-context', 'support'],
    overlayContexts: ['/accounts'],
    metrics: [
      { label: 'Workspace', value: 'Compatibility detail' },
      { label: 'Covers', value: 'Services and billing records' },
      { label: 'Primary Action', value: 'Return to portal home for new work' },
    ],
    sections: [
      {
        id: 'purpose',
        title: 'What The Account Workspace Is For',
        paragraphs: [
          'The account workspace still exposes backend-facing account records when a compatibility route or saved bookmark opens one directly.',
          'It is no longer the main operational landing page for end users. New work should start from portal home and tenant context instead.',
        ],
      },
      {
        id: 'navigation',
        title: 'How To Navigate',
        paragraphs: [
          'Use account pages when you need a direct compatibility record for services or billing history.',
          'Legacy ticket flows are retired, so support questions should move through the Support route instead of a ticket tab.',
        ],
      },
    ],
  },
  {
    articleId: 'account-services',
    title: 'Services',
    summary:
      'Review compatibility service records attached to the current account when support or billing work depends on them.',
    category: 'tenant-workspaces',
    keywords: ['services', 'service list', 'service status', 'internet service', 'account services'],
    relatedRoutes: ['/dashboard'],
    relatedArticleIds: ['accounts-overview', 'billing-payments', 'support'],
    overlayContexts: ['/accounts'],
    metrics: [
      { label: 'Object', value: 'Account services' },
      { label: 'Used For', value: 'Operational visibility' },
      { label: 'Common Follow-up', value: 'Open support guidance' },
    ],
    sections: [
      {
        id: 'viewing',
        title: 'Viewing Services',
        paragraphs: [
          'The Services view shows the service records linked to the current account, including descriptive and billing-adjacent fields needed for compatibility workflows.',
          'Use this page to confirm which service records exist before opening support guidance or reviewing billing history.',
        ],
      },
      {
        id: 'what-to-check',
        title: 'What To Check',
        paragraphs: [
          'Look for the service description, status, and important dates first. Those fields usually answer whether you are looking at the expected service record.',
          'If the service list looks incomplete, confirm the tenant context and account route before assuming data is missing from the backend.',
        ],
      },
    ],
  },
  {
    articleId: 'billing-payments',
    title: 'Billing and Payments',
    summary:
      'Review balances, payment history, and account billing state from the compatibility billing workspace when that backend record still matters.',
    category: 'tenant-workspaces',
    keywords: ['billing', 'payment', 'pay bill', 'balance', 'invoice', 'history'],
    relatedRoutes: ['/dashboard'],
    relatedArticleIds: ['accounts-overview', 'support'],
    overlayContexts: ['/accounts'],
    metrics: [
      { label: 'Object', value: 'Billing compatibility workspace' },
      { label: 'Shows', value: 'Balances and payment history' },
      { label: 'Common Question', value: 'Where is the backend billing record?' },
    ],
    sections: [
      {
        id: 'summary',
        title: 'Billing Summary',
        paragraphs: [
          'Use the billing summary to review the current balance and aging buckets when a compatibility billing record still matters.',
          'The summary is meant to answer what is currently due and whether the balance is concentrated in recent or older charges.',
        ],
      },
      {
        id: 'history',
        title: 'Payment History',
        paragraphs: [
          'Payment history helps confirm whether a prior payment was recorded and when it was posted.',
          'If you are reconciling a recent payment, compare the last payment details on the overview with the deeper billing history on the billing page.',
        ],
      },
      {
        id: 'before-paying',
        title: 'Before Taking Action',
        paragraphs: [
          'Confirm that you are on the correct account and tenant before reviewing or discussing a billing record.',
          'If the expected payment entry is not visible, refresh the route after backend state settles or confirm with support whether processing is still in flight.',
        ],
      },
    ],
  },
  {
    articleId: 'support',
    title: 'Support',
    summary:
      'The Support route is the current tenant-native placeholder for support workflows. Legacy ticket routes are retired and now redirect here.',
    category: 'support',
    keywords: ['support', 'tickets', 'case management', 'help', 'legacy tickets'],
    relatedRoutes: ['/support'],
    relatedArticleIds: ['getting-started', 'portal-home', 'tenant-context'],
    overlayContexts: ['/support', '/accounts'],
    metrics: [
      { label: 'Workspace', value: 'Support placeholder' },
      { label: 'Current State', value: 'Guidance only' },
      { label: 'Future Direction', value: 'Tenant-native support workflow' },
    ],
    sections: [
      {
        id: 'current-state',
        title: 'What Support Does Today',
        paragraphs: [
          'The current Support page is a stub. It gives users a stable route for help guidance while the new tenant-native support service does not yet exist.',
          'Legacy account ticket pages are retired, so old ticket links redirect into this route instead of opening ticket detail screens.',
        ],
      },
      {
        id: 'what-to-do',
        title: 'What To Do Instead',
        paragraphs: [
          'Use Help for documentation, Setup for onboarding blockers, and tenant context checks when a workspace is unexpectedly missing.',
          'When the future support service is implemented, this route will become the canonical intake and case management surface.',
        ],
      },
    ],
  },
  {
    articleId: 'maps',
    title: 'Maps',
    summary:
      'The portal Maps area gives tenant-scoped access to map sources, styles, custom maps, sprites, and glyphs. Use it when your tenant manages mapping assets.',
    category: 'maps',
    keywords: ['maps', 'styles', 'sources', 'custom maps', 'sprites', 'glyphs'],
    relatedRoutes: ['/maps/sources', '/maps/styles', '/maps/custom-maps'],
    relatedArticleIds: ['tenant-context'],
    overlayContexts: ['/maps/sources', '/maps/styles', '/maps/custom-maps', '/maps/sprites', '/maps/glyphs'],
    metrics: [
      { label: 'Workspace', value: 'Tenant map assets' },
      { label: 'Includes', value: 'Sources, styles, custom maps' },
      { label: 'Scope', value: 'Tenant-owned data' },
    ],
    sections: [
      {
        id: 'overview',
        title: 'What Lives In Maps',
        paragraphs: [
          'Maps in the portal are tenant-scoped asset and authoring surfaces. They are used to manage mapping resources rather than to browse a public help site or standalone GIS product.',
          'Use Sources for raw inputs, Styles for presentation rules, Custom Maps for composed map experiences, and Sprites or Glyphs for supporting assets.',
        ],
      },
      {
        id: 'route-choice',
        title: 'Choosing The Right Route',
        paragraphs: [
          'Start with Sources when the data origin is the main concern. Start with Styles when the visual presentation is the primary task.',
          'Use Custom Maps when you need a map experience assembled for a tenant workflow rather than just a styling asset.',
        ],
      },
    ],
  },
  {
    articleId: 'sales-map',
    title: 'Sales Map',
    summary:
      'The Sales map helps portal users explore sales-oriented geographic data and sales workflows within the same tenant-scoped shell.',
    category: 'sales',
    keywords: ['sales', 'sales map', 'coverage', 'map', 'sales workflow'],
    relatedRoutes: ['/sales'],
    relatedArticleIds: ['maps'],
    overlayContexts: ['/sales'],
    metrics: [
      { label: 'Workspace', value: 'Sales map' },
      { label: 'Focus', value: 'Sales geography' },
      { label: 'Context', value: 'Tenant-scoped' },
    ],
    sections: [
      {
        id: 'what',
        title: 'What The Sales Map Is For',
        paragraphs: [
          'Use the Sales map when you need a geographic view of sales-oriented context instead of the broader asset-management surfaces in Maps.',
          'It stays inside the same tenant-scoped portal shell so the surrounding navigation and permissions remain consistent with the rest of the product.',
        ],
      },
      {
        id: 'when',
        title: 'When To Use It',
        paragraphs: [
          'Open Sales when the goal is opportunity, serviceability, or territory-oriented exploration rather than editing a reusable map asset.',
          'If the task is about long-lived map sources or styles, move to the Maps area instead.',
        ],
      },
    ],
  },
  {
    articleId: 'api-gateway',
    title: 'API Gateway Access',
    summary:
      'Use the API Gateway area to manage API access and open the developer console within the current portal session when your tenant and permissions allow it.',
    category: 'automation',
    keywords: ['api', 'api gateway', 'developer console', 'api access', 'keys', 'automation'],
    relatedRoutes: ['/api-gateway/access', '/api-gateway/console'],
    relatedArticleIds: ['settings'],
    overlayContexts: ['/api-gateway/access', '/api-gateway/console'],
    metrics: [
      { label: 'Workspace', value: 'API Gateway' },
      { label: 'Includes', value: 'Access and console' },
      { label: 'Permission Model', value: 'Tenant feature gated' },
    ],
    sections: [
      {
        id: 'overview',
        title: 'What This Area Covers',
        paragraphs: [
          'API Access is the management surface for API-gateway-related tenant capabilities. The Developer Console lets a signed-in portal user exercise routes through the same authenticated browser session.',
          'Whether the API Gateway area appears depends on tenant features and permissions, so it may be visible for one tenant and absent or denied for another.',
        ],
      },
      {
        id: 'console',
        title: 'Developer Console',
        paragraphs: [
          'The Developer Console is meant for authenticated developer testing through the portal’s same-origin BFF path.',
          'Use it when you need to inspect gateway routes or exercise requests without leaving the portal session.',
        ],
      },
    ],
  },
  {
    articleId: 'settings',
    title: 'Settings',
    summary:
      'Settings holds per-user portal preferences such as appearance and shell behavior. It is where you adjust your personal portal experience rather than tenant-owned business data.',
    category: 'settings',
    keywords: ['settings', 'preferences', 'theme', 'appearance', 'portal settings'],
    relatedRoutes: ['/settings'],
    relatedArticleIds: ['getting-started', 'tenant-context'],
    overlayContexts: ['/settings'],
    metrics: [
      { label: 'Object', value: 'Per-user preferences' },
      { label: 'Scope', value: 'Your portal experience' },
      { label: 'Common Tasks', value: 'Appearance and layout' },
    ],
    sections: [
      {
        id: 'scope',
        title: 'What Settings Control',
        paragraphs: [
          'Settings are personal preferences for how the portal behaves and appears in your browser experience.',
          'These controls are different from tenant-owned configuration or account-owned business records.',
        ],
      },
      {
        id: 'shared-patterns',
        title: 'Shared Settings Patterns',
        paragraphs: [
          'The portal settings experience follows the shared shell and feedback patterns used across the broader frontend system.',
          'If you use both portal and dashboard, many settings concepts should feel structurally similar even when the exact available preferences differ.',
        ],
      },
    ],
  },
];

function routeForCommerceSection(sectionId: string) {
  return buildCommerceSectionRoute(sectionId);
}

const commerceArticles: PortalHelpArticle[] = getCommerceHelpArticles().map(article => ({
  articleId: `commerce-${article.sectionId}`,
  title: article.title,
  summary: article.summary,
  category: 'commerce',
  keywords: [
    article.title,
    article.sectionId,
    'commerce',
    ...article.relatedSections,
  ],
  relatedRoutes: [routeForCommerceSection(article.sectionId)],
  relatedArticleIds: article.relatedSections.map(sectionId => `commerce-${sectionId}`),
  overlayContexts: [routeForCommerceSection(article.sectionId)],
  metrics: article.metrics,
  sections: article.sections,
}));

const portalHelpArticles = [...baseArticles, ...commerceArticles];

const portalHelpArticleMap = new Map(portalHelpArticles.map(article => [article.articleId, article]));

export function getPortalHelpArticles() {
  return portalHelpArticles;
}

export function getPortalHelpArticle(articleId: string | null | undefined): PortalHelpArticle | null {
  if (!articleId) {
    return null;
  }
  return portalHelpArticleMap.get(articleId) ?? null;
}

export function getPortalHelpArticlesByCategory() {
  return portalHelpArticles.reduce<Record<string, PortalHelpArticle[]>>((acc, article) => {
    const key = article.category;
    acc[key] = acc[key] ?? [];
    acc[key].push(article);
    return acc;
  }, {});
}
